import { useEffect, useState } from "react";
import { articles, categories, type Article } from "@/mocks/blog";

function ArticleModal({ article, onClose }: { article: Article | null; onClose: () => void }) {
  useEffect(() => {
    if (!article) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [article, onClose]);

  if (!article) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-foreground-950/40 p-0 sm:items-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="nice-scroll relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-lg bg-background-50 shadow-xl sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
          aria-label="关闭"
        >
          <i className="ri-close-line text-xl" aria-hidden="true"></i>
        </button>

        <div className="relative h-52 w-full overflow-hidden sm:h-64">
          <img src={article.cover} alt={article.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 to-transparent"></div>
        </div>

        <div className="px-5 pb-10 pt-2 sm:px-8">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-primary-100 px-2.5 py-0.5 font-semibold text-primary-700">{article.category}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-calendar-line" aria-hidden="true"></i>{article.date}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-time-line" aria-hidden="true"></i>{article.readTime}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{article.views}</span>
          </div>

          <h3 className="mt-4 font-display text-xl font-bold leading-snug text-foreground-950 sm:text-2xl">
            {article.title}
          </h3>
          <p className="mt-1 text-sm text-foreground-500">
            作者：<span className="font-semibold text-foreground-700">{article.author}</span> · 个人视角 · 主观看法
          </p>

          <hr className="my-5 border-background-200" />

          <div className="space-y-4">
            {article.body.map((para, i) => (
              <p key={i} className="text-[15px] leading-[1.9] text-foreground-700">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-6 rounded-lg bg-accent-50 p-4 text-sm text-accent-800">
            <i className="mr-1 ri-question-mark" aria-hidden="true"></i>
            以上纯属我的主观看法，仅供参考。事件本身请以多方信源为准，欢迎理性讨论，拒绝无脑站队。
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ArticlesSection() {
  const [active, setActive] = useState("全部");
  const [selected, setSelected] = useState<Article | null>(null);

  const list = active === "全部" ? articles : articles.filter((a) => a.category === active);

  return (
    <section id="articles" className="bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">MY OPINIONS</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">
            观点随笔 · <span className="text-primary-600">我的看法与主观评价</span>
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            不随大流、不写通稿，只从一个普通人的真实处境出发。点开任何一篇，都是我掏心窝子的吃瓜心得。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                active === c
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-200 bg-background-100 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {list.map((article) => (
            <article
              key={article.id}
              onClick={() => setSelected(article)}
              className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:shadow-[0_10px_30px_oklch(var(--background-300)/0.4)]"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={article.cover}
                  alt={`${article.title} - 吾爱吃瓜观点随笔`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-0.5 text-xs font-semibold text-primary-700 backdrop-blur">
                  {article.category}
                </span>
                {article.highlight && (
                  <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-fire-fill" aria-hidden="true"></i>
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1"><i className="ri-calendar-line" aria-hidden="true"></i>{article.date}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-time-line" aria-hidden="true"></i>{article.readTime}</span>
                  <span className="ml-auto inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{article.views}</span>
                </div>
                <h3 className="mt-3 font-display text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                  {article.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 self-start text-sm font-semibold text-primary-600 group-hover:text-primary-700">
                  展开全文 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>

      <ArticleModal article={selected} onClose={() => setSelected(null)} />
    </section>
  );
}